import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Image, Upload, X, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ChatPhotoUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  recipientId?: string;
  onPhotoUploaded: (photoUrl: string, messageId: string) => void;
}

export const ChatPhotoUpload = ({ 
  open, 
  onOpenChange, 
  jobId, 
  recipientId,
  onPhotoUploaded 
}: ChatPhotoUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      // Get current user profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Generate unique filename
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${jobId}/${fileName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('chat-photos')
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('chat-photos')
        .getPublicUrl(filePath);

      // Determine recipient based on context
      let messageRecipientId = recipientId;
      
      if (!messageRecipientId) {
        // Fallback: try to get job details to determine recipient
        const { data: jobData } = await supabase
          .from('jobs')
          .select('customer_id, tradesman_id')
          .eq('id', jobId)
          .single();
          
        if (jobData) {
          // If current user is customer, send to tradesman; if tradesman, send to customer
          const { data: currentProfile } = await supabase
            .from('profiles')
            .select('user_type')
            .eq('user_id', user.id)
            .single();
            
          if (currentProfile?.user_type === 'customer') {
            messageRecipientId = jobData.tradesman_id;
          } else {
            messageRecipientId = jobData.customer_id;
          }
        }
      }

      // Ensure we have a valid recipient
      if (!messageRecipientId) {
        throw new Error('Unable to determine message recipient for this chat');
      }

      // Create message with photo
      // Create message with photo via edge function (handles auth/RLS)
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          job_id: jobId,
          recipient_id: messageRecipientId,
          content: '[Photo]',
          message_type: 'photo',
          photo_url: urlData.publicUrl
        }
      });

      if (sendError) throw sendError;

      const createdMessageId = (sendResult as any)?.id || (sendResult as any)?.message?.id;
      if (!createdMessageId) {
        throw new Error('Photo message sent but no message id returned');
      }


      // Create chat photo record
      const { error: photoError } = await supabase
        .from('chat_photos')
        .insert({
          message_id: createdMessageId,
          job_id: jobId,
          file_path: filePath,
          file_name: selectedFile.name,
          file_size: selectedFile.size,
          content_type: selectedFile.type,
          uploaded_by: profile.id
        });

      if (photoError) throw photoError;

      onPhotoUploaded(urlData.publicUrl, createdMessageId);
      
      // Reset form
      setSelectedFile(null);
      setPreviewUrl('');
      onOpenChange(false);
      
      toast({
        title: "Photo uploaded",
        description: "Your photo has been shared successfully",
      });

    } catch (error: any) {
      console.error('Error uploading photo:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload photo",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Share Photo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!selectedFile ? (
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Image className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                Select an image to share in this conversation
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choose Photo
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg border"
                />
                <Button
                  onClick={clearSelection}
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p>{selectedFile.name}</p>
                <p>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={clearSelection}
                  variant="outline"
                  className="flex-1"
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  className="flex-1"
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Share Photo
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};